// spellStudioEngine.js
// Combat PRO - Custom Spell Studio foundation runtime.
//
// Stage 50 (Custom Spell Studio - Phase 1: Foundation).
//
// Pure, data-in / data-out module. No React, no DOM, no window, no document,
// no localStorage, no Context, no JSX, no rendering, no logging, no external
// mutation. This module owns the foundational shared spell model, the editor
// data structures, the duration math, and the authoring-time validation that
// later Custom Spell Studio stages (editor UI, CRUD, persistence) build upon.
//
// This module duplicates no locked runtime. It imports nothing from
// spellEngine / spellCastEngine / spellAiEngine and re-implements no casting,
// scoring, or recommendation logic. It is verbatim-faithful to the legacy
// custom-spell authoring code in battletracker.html, with all DOM reads,
// network persistence, and SPELLS_DB / select mutation removed (those are
// deferred to later stages per the Stage 50 brief).

// ---------------------------------------------------------------------------
// Editor data structures (enumerations from the legacy modal selects)
// ---------------------------------------------------------------------------

export var SPELL_SCHOOLS = [
    'Abjuration', 'Conjuration', 'Divination', 'Enchantment', 'Evocation',
    'Illusion', 'Necromancy', 'Transmutation', 'Other'
];

export var SPELL_TYPES = ['damage', 'control', 'heal', 'buff', 'utility'];

export var SPELL_LEVELS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];

export var SAVE_TYPES = ['', 'STR', 'DEX', 'CON', 'INT', 'WIS', 'CHA'];

export var DURATION_TYPES = ['instant', 'timed', 'concentration', 'dispel', 'special'];

export var DURATION_UNITS = ['rounds', 'minutes', 'hours', 'days'];

// value -> rounds multiplier (legacy vi_computeDurationRounds)
export var DURATION_UNIT_ROUNDS = {
    rounds: 1,
    minutes: 10,
    hours: 600,
    days: 14400
};

// Concentration select: value (rounds) -> visible label (legacy modal options).
// Order preserved so the first key whose rounds >= target wins in decompose.
export var CONCENTRATION_OPTIONS = [
    { value: 1, label: 'Up to 1 Round' },
    { value: 10, label: 'Up to 1 Minute' },
    { value: 100, label: 'Up to 10 Minutes' },
    { value: 600, label: 'Up to 1 Hour' }
];

export var DISPEL_ROUNDS = 9999;

// ---------------------------------------------------------------------------
// ID generation (verbatim port of legacy generateSpellId)
// ---------------------------------------------------------------------------

export function createSpellId() {
    return 'spell_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5);
}

// ---------------------------------------------------------------------------
// Default blank editor fields (legacy vi_openAddSpellModal reset values)
// ---------------------------------------------------------------------------

export function defaultSpellFields() {
    return {
        name: '',
        level: '1',
        school: 'Evocation',
        spellType: 'damage',
        castingTime: '1 action',
        range: '',
        components: '',
        damageFormula: '',
        saveType: '',
        saveEffect: '',
        areaOfEffect: '',
        appliesCondition: '',
        desc: '',
        durationType: 'instant',
        durationUnit: 'rounds',
        durationValue: '1',
        durationConcSelect: '10',
        durationSpecialText: ''
    };
}

// ---------------------------------------------------------------------------
// Duration math (pure ports of vi_computeDurationRounds and the duration
// label IIFE / reconstruction logic in vi_loadSpellIntoModal)
// ---------------------------------------------------------------------------

function concLabelForValue(value) {
    var v = String(value);
    for (var i = 0; i < CONCENTRATION_OPTIONS.length; i++) {
        if (String(CONCENTRATION_OPTIONS[i].value) === v) {
            return CONCENTRATION_OPTIONS[i].label;
        }
    }
    return 'Up to 1 Minute';
}

// fields: { durationType, durationUnit, durationValue, durationConcSelect }
export function computeDurationRounds(fields) {
    var dt = fields.durationType;
    var rounds = 0;
    if (dt === 'instant') {
        rounds = 0;
    } else if (dt === 'timed') {
        var val = parseInt(fields.durationValue, 10) || 0;
        var unit = fields.durationUnit;
        var mult = DURATION_UNIT_ROUNDS[unit];
        rounds = mult ? val * mult : 0;
    } else if (dt === 'concentration') {
        rounds = parseInt(fields.durationConcSelect, 10) || 10;
    } else if (dt === 'dispel') {
        rounds = DISPEL_ROUNDS;
    } else if (dt === 'special') {
        rounds = 0;
    }
    return rounds;
}

// fields: { durationType, durationUnit, durationValue, durationConcSelect, durationSpecialText }
// Builds the human-readable duration string (legacy saveCustomSpell duration IIFE).
export function buildDurationLabel(fields) {
    var dt = fields.durationType;
    if (dt === 'instant') return 'Instantaneous';
    if (dt === 'dispel') return 'Until Dispelled';
    if (dt === 'special') {
        return (fields.durationSpecialText || '').trim() || 'Special';
    }
    if (dt === 'concentration') {
        return concLabelForValue(fields.durationConcSelect);
    }
    var val = fields.durationValue || '1';
    var unit = fields.durationUnit;
    var unitLabel;
    if (unit === 'rounds') {
        unitLabel = (val === '1') ? 'round' : 'rounds';
    } else {
        unitLabel = (val === '1') ? unit.replace(/s$/, '') : unit;
    }
    return val + ' ' + unitLabel;
}

// Inverse of the duration math: given a stored spell, reconstruct the editor
// duration field values (legacy vi_loadSpellIntoModal duration reconstruction).
// Returns string-valued fields to match the editor field contract.
export function decomposeDuration(spell) {
    var dr = spell.durationRounds;
    var durText = (spell.duration || '').toLowerCase();
    var out = {
        durationType: 'instant',
        durationUnit: 'rounds',
        durationValue: '1',
        durationConcSelect: '10',
        durationSpecialText: ''
    };
    if (spell.concentration) {
        out.durationType = 'concentration';
        var bestConc = 10;
        for (var ci = 0; ci < CONCENTRATION_OPTIONS.length; ci++) {
            if (dr <= CONCENTRATION_OPTIONS[ci].value) {
                bestConc = CONCENTRATION_OPTIONS[ci].value;
                break;
            }
        }
        out.durationConcSelect = String(bestConc);
    } else if (dr === DISPEL_ROUNDS || durText.indexOf('dispel') !== -1) {
        out.durationType = 'dispel';
    } else if (dr === 0 && (durText.indexOf('instant') !== -1 || !dr)) {
        out.durationType = 'instant';
    } else if (dr > 0) {
        out.durationType = 'timed';
        if (dr % 14400 === 0 && dr >= 14400) {
            out.durationUnit = 'days';
            out.durationValue = String(dr / 14400);
        } else if (dr % 600 === 0 && dr >= 600) {
            out.durationUnit = 'hours';
            out.durationValue = String(dr / 600);
        } else if (dr % 10 === 0 && dr >= 10) {
            out.durationUnit = 'minutes';
            out.durationValue = String(dr / 10);
        } else {
            out.durationUnit = 'rounds';
            out.durationValue = String(dr);
        }
    } else {
        out.durationType = 'special';
        out.durationSpecialText = spell.duration || '';
    }
    return out;
}

// ---------------------------------------------------------------------------
// Spell <-> editor-fields mapping
// ---------------------------------------------------------------------------

// Populate editor fields from an existing spell model (legacy field population
// in vi_loadSpellIntoModal, DOM removed). Returns a full fields object.
export function spellToFields(spell) {
    var dur = decomposeDuration(spell);
    return {
        name: spell.name || '',
        level: spell.level != null ? String(spell.level) : '1',
        school: spell.school || 'Evocation',
        spellType: spell.spellType || 'damage',
        castingTime: spell.castingTime || '1 action',
        range: spell.range || '',
        components: spell.components || '',
        damageFormula: spell.damageFormula || '',
        saveType: spell.saveType || '',
        saveEffect: spell.saveEffect || '',
        areaOfEffect: spell.areaOfEffect || '',
        appliesCondition: spell.appliesCondition || '',
        desc: spell.desc || '',
        durationType: dur.durationType,
        durationUnit: dur.durationUnit,
        durationValue: dur.durationValue,
        durationConcSelect: dur.durationConcSelect,
        durationSpecialText: dur.durationSpecialText
    };
}

// Shared spell model factory (verbatim port of the spellData construction in
// legacy vi_saveCustomSpell, with DOM reads, persistence, and SPELLS_DB /
// select mutation removed). Pure: returns a new spell object.
//
// fields: editor field contract (see defaultSpellFields).
// options: { editingSpell, createId }
//   editingSpell - the spell currently loaded in the editor (or null).
//   createId     - id factory (defaults to createSpellId); injected for tests.
export function buildCustomSpell(fields, options) {
    var opts = options || {};
    var editingSpell = opts.editingSpell || null;
    var createId = opts.createId || createSpellId;

    var name = (fields.name || '').trim();
    var level = parseInt(fields.level, 10);
    var type = fields.spellType;

    var rawFormula = (fields.damageFormula || '').trim();
    var rawSave = (fields.saveType || '').trim();
    var rawAoe = (fields.areaOfEffect || '').trim();
    var rawCond = (fields.appliesCondition || '').trim();
    var isConc = fields.durationType === 'concentration';

    var spellData = {
        name: name,
        level: isNaN(level) ? 0 : level,
        school: fields.school || 'Other',
        castingTime: (fields.castingTime || '').trim() || '1 action',
        range: (fields.range || '').trim() || 'Self',
        duration: buildDurationLabel(fields),
        components: (fields.components || '').trim() || '',
        desc: (fields.desc || '').trim(),
        higherLevel: '',
        ritual: false,
        concentration: isConc,
        spellType: type,
        damageFormula: rawFormula || null,
        saveType: rawSave || null,
        saveEffect: rawSave ? ((fields.saveEffect || '').trim() || 'none') : null,
        areaOfEffect: rawAoe || null,
        appliesCondition: rawCond || null,
        durationRounds: computeDurationRounds(fields),
        source: 'user'
    };

    var isEditingUser = editingSpell && editingSpell.source === 'user';
    if (isEditingUser) {
        spellData.id = editingSpell.id;
    } else {
        spellData.id = createId();
    }
    return spellData;
}

// ---------------------------------------------------------------------------
// Validation framework
// ---------------------------------------------------------------------------

// Name validation (legacy vi_saveCustomSpell required + collision checks).
// spellsDb: array of existing spells; editingId: id allowed to collide (self).
// Returns { valid, error }.
export function validateSpellName(name, spellsDb, editingId) {
    var trimmed = (name || '').trim();
    if (!trimmed) {
        return { valid: false, error: 'Spell Name is required.' };
    }
    var db = Array.isArray(spellsDb) ? spellsDb : [];
    var lower = trimmed.toLowerCase();
    var collision = null;
    for (var i = 0; i < db.length; i++) {
        var s = db[i];
        if (s && s.name && s.name.toLowerCase() === lower && s.id !== editingId) {
            collision = s;
            break;
        }
    }
    if (collision) {
        return { valid: false, error: 'A spell with this name already exists. Please rename your spell.' };
    }
    return { valid: true, error: '' };
}

function isFilled(value) {
    return value !== null && value !== undefined && value.toString().trim() !== '';
}

// Step-completion evaluation (legacy vi_spellIsFilled + vi_evaluateStepCompletion).
// Returns an object keyed 1..5 of booleans. Pure: reads only the fields object.
export function evaluateStepCompletion(fields) {
    var f = fields || {};
    var progress = { 1: false, 2: false, 3: false, 4: false, 5: false };
    progress[1] = isFilled(f.name);
    progress[2] = progress[1] && isFilled(f.castingTime) && isFilled(f.range) && isFilled(f.components);
    progress[3] = progress[2];
    progress[4] = progress[3];
    progress[5] = progress[4] && isFilled(f.desc);
    return progress;
}

// Whether the spell may be saved in guided mode (legacy: save enabled when
// step 5 is complete). Quick mode bypass is a UI concern, deferred.
export function canSaveSpell(fields) {
    return evaluateStepCompletion(fields)[5] === true;
}

// ---------------------------------------------------------------------------
// User-spell normalization (pure parts of legacy vi_mergeCustomSpells)
// ---------------------------------------------------------------------------

// Normalize a list of user spells: keep only entries with a name, ensure id
// and source. Returns a NEW array of NEW objects; inputs are not mutated.
// SPELLS_DB merge and select rebuild are runtime integration, deferred.
export function normalizeUserSpells(userSpells, createId) {
    var make = createId || createSpellId;
    var list = Array.isArray(userSpells) ? userSpells : [];
    var out = [];
    for (var i = 0; i < list.length; i++) {
        var sp = list[i];
        if (sp && sp.name) {
            var copy = Object.assign({}, sp);
            if (!copy.id) copy.id = make();
            if (!copy.source) copy.source = 'user';
            out.push(copy);
        }
    }
    return out;
}

// Merge normalized user spells into a base spell list by unique name.
// Returns a NEW array; inputs are not mutated (legacy dedup-by-name rule).
export function mergeSpellLists(spellsDb, normalizedUserSpells) {
    var base = Array.isArray(spellsDb) ? spellsDb.slice() : [];
    var adds = Array.isArray(normalizedUserSpells) ? normalizedUserSpells : [];
    for (var i = 0; i < adds.length; i++) {
        var sp = adds[i];
        var exists = false;
        for (var j = 0; j < base.length; j++) {
            if (base[j] && base[j].name === sp.name) { exists = true; break; }
        }
        if (!exists) base.push(sp);
    }
    return base;
}
